import React from 'react';
import { type IntlShape, defineMessages } from 'react-intl-next';

import { DefaultExtensionProvider } from '@atlaskit/editor-common/extensions';

import { LoadableAfterPaint } from '@confluence/loadable';

import {
	type CustomSitesManifestArguments,
	getExtensionManifest,
} from '../shared-components/customSitesManifestBuilder';

import { searchExtensionKey, searchExtensionType, searchVersion1 } from './searchConstants';
import { type SearchExtensionParameters, type SearchExtensionProps } from './SearchExtension';

const SearchExtension = LoadableAfterPaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-SearchExtension" */ './SearchExtension'))
			.SearchExtension,
});

// TODO: https://hello.jira.atlassian.cloud/browse/CCIR-388
// Finalize the copies and remove non-finals
const i18n = defineMessages({
	title: {
		id: 'custom-sites-extensions.search.manifest.macro-title.non-final',
		defaultMessage: 'Custom search',
		description:
			'Title of company hub search macro to be displayed in toolbar/shortcut macros dropdown.',
	},
	description: {
		id: 'custom-sites-extensions.search.manifest.macro-description.non-final',
		defaultMessage: 'Add a filtered search bar',
		description:
			'Description of company hub search macro to be displayed in toolbar/shortcut macros dropdown.',
	},
});

type SearchExtensionProvidertArguments = {
	intl: IntlShape;
	contentId: SearchExtensionProps['contentId'];
	editorActions?: SearchExtensionProps['editorActions'];
	openConfigPanel?: SearchExtensionProps['openConfigPanel'];
	setUpsellModalSource?: CustomSitesManifestArguments<SearchExtensionParameters>['setUpsellModalSource'];
	createAnalyticsEvent: SearchExtensionProps['createAnalyticsEvent'];
};

export const searchExtensionProvider = async ({
	intl,
	contentId,
	editorActions,
	openConfigPanel,
	setUpsellModalSource,
	createAnalyticsEvent,
}: SearchExtensionProvidertArguments) => {
	return new DefaultExtensionProvider([
		getExtensionManifest({
			key: searchExtensionKey,
			type: searchExtensionType,
			title: intl.formatMessage(i18n.title),
			description: intl.formatMessage(i18n.description),
			source: 'premiumSearchInsert',
			setUpsellModalSource,
			icons: {
				'48': async () =>
					(
						await import(
							/* webpackChunkName: "loadable-SearchMacroIcon" */
							'@confluence/icons/entry-points/SearchMacroIcon'
						)
					).SearchMacroIcon,
			},

			parameters: {
				extensionTitle: intl.formatMessage(i18n.title),
				version: searchVersion1,
			},

			update: async () => {
				const selectedNode = editorActions?.getSelectedNode()?.toJSON();
				const selectedLocalId = selectedNode?.attrs?.localId;

				if (!Boolean(selectedLocalId) && openConfigPanel) {
					openConfigPanel(selectedLocalId);
				}
			},

			render:
				async () =>
				({ node }) => {
					return (
						<SearchExtension
							contentId={contentId}
							editorActions={editorActions}
							extensionNode={node}
							openConfigPanel={openConfigPanel}
							createAnalyticsEvent={createAnalyticsEvent}
						/>
					);
				},
		}),
	]);
};
